import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { SeriesBadge } from "@/components/SeriesBadge";
import awningUnderneathAsset from "@/assets/awning-underneath-frame.jpg.asset.json";
import sunbrellaLogo from "@/assets/sunbrella-logo.png.asset.json";
import sunbrellaFabric from "@/assets/sunbrella-beaufort-captain-navy.jpg.asset.json";
import { watchVideos } from "@/data/watchVideos";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import hoa3dDesign from "@/assets/hoa-3d-awning-design.jpg.asset.json";
import rangerCareBadge from "@/assets/ranger-care-homestead-badge.png.asset.json";
import rangerCareTech from "@/assets/ranger-care-technician-measuring.jpg.asset.json";

import shareImage from "@/assets/awning-blue-canvas.jpg";
import backyardMetalCover from "@/assets/back-porch-awning-sunbrella-smoke.jpg";
import blackStripeWindowAwnings from "@/assets/residential-black-stripe-window-awnings.jpg";
import customMeasuring from "@/assets/residential-awning-custom-measuring.jpg";
import beaufortHome from "@/assets/beaufort-black-white-home-awning.jpg";

const WIDTH_OPTIONS = [
  "Under 6 ft",
  "6 – 8 ft",
  "8 – 10 ft",
  "10 – 12 ft",
  "12 – 16 ft",
  "16 – 20 ft",
  "20 – 30 ft",
  "30 ft or wider",
  "Not sure — approximate is fine",
];

const HEIGHT_OPTIONS = [
  "Under 7 ft",
  "7 – 8 ft",
  "8 – 9 ft",
  "9 – 10 ft",
  "10 – 12 ft",
  "12 – 14 ft",
  "14 ft or taller",
  "Not sure — approximate is fine",
];

const PROJECTION_OPTIONS = [
  "Under 3 ft",
  "3 – 4 ft",
  "4 – 6 ft",
  "6 – 8 ft",
  "8 – 10 ft",
  "10 – 12 ft",
  "12 ft or more",
  "Not sure — approximate is fine",
];

const SERIES_OPTIONS = [
  { value: "Alamo Series — Residential", label: "Alamo Series™ — Residential", defaultChecked: true },
  { value: "Longhorn Series — Commercial", label: "Longhorn Series™ — Commercial" },
  { value: "Rio Grande Series — Hospitality (Hotels & Resorts)", label: "Rio Grande Series™ — Hospitality (Hotels & Resorts)" },
  { value: "Republic Series — Government / Schools", label: "Republic Series™ — Government / Schools" },
];

const FABRIC_OPTIONS = [
  "Sunbrella®",
  "Tempotest® Shade",
  "Serge Ferrari 502 (Waterproof)",
  "Help me decide",
];

function AwningExtras() {
  const selectClass = "w-full border border-input bg-white px-4 py-3 text-sm focus:border-navy-deep focus:outline-none";
  return (
    <div className="space-y-4 border-l-4 border-navy-deep/20 bg-cream/40 p-4">
      <p className="text-xs text-navy-deep/70">
        Approximate sizes are totally fine — this just gets the process started. We'll confirm exact measurements on-site.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="awningWidth" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Wide?</label>
          <select id="awningWidth" name="awningWidth" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select width…</option>
            {WIDTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="awningHeight" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Tall in Back?</label>
          <select id="awningHeight" name="awningHeight" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select height…</option>
            {HEIGHT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="awningProjection" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">How Far Out From Wall?</label>
          <select id="awningProjection" name="awningProjection" defaultValue="" className={`mt-1 ${selectClass}`}>
            <option value="" disabled>Select projection…</option>
            {PROJECTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-navy-deep/20 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-navy-deep">Photos to attach (below)</div>
        <ul className="mt-2 space-y-1 text-sm text-navy-deep/80">
          <li>• Right-side photo</li>
          <li>• Left-side photo</li>
          <li>• Front-facing photo</li>
          <li>• <strong>For an existing awning re-cover:</strong> a photo of the underneath / frame</li>
        </ul>
        <p className="mt-2 text-xs text-navy-deep/60">Use the "Attach Photos / Files" button further down to upload.</p>
      </div>

      <fieldset className="border border-navy-deep/20 bg-white p-4">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-navy-deep">Which series fits your project?</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          {SERIES_OPTIONS.map((s) => (
            <label key={s.value} className="flex items-start gap-2 text-sm text-navy-deep">
              <input
                type="checkbox"
                name="awningSeries"
                value={s.value}
                defaultChecked={s.defaultChecked}
                className="mt-1 h-4 w-4 border-navy-deep/40 text-navy-deep focus:ring-navy-deep"
              />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-navy-deep/60">Residential projects belong to the Alamo Series™ — pre-selected for you.</p>
      </fieldset>

      <div>
        <label htmlFor="awningFabric" className="block text-xs font-semibold uppercase tracking-wider text-navy-deep">Preferred Fabric</label>
        <select id="awningFabric" name="awningFabric" defaultValue="" className={`mt-1 ${selectClass}`}>
          <option value="" disabled>Select fabric…</option>
          {FABRIC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

function AlamoSeriesCallout() {
  return (
    <section
      className="relative bg-cream py-12 md:py-16"
      style={{
        backgroundImage: `url(${beaufortHome})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      aria-label="Texas home with a Sunbrella Beaufort black and white striped awning"
    >
      <div className="absolute inset-0 bg-navy-deep/70" aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-6">
        <Link
          to="/alamo-series"
          className="group grid items-center gap-8 border-2 border-white/20 bg-white/5 p-6 shadow-card backdrop-blur-sm transition hover:border-red/60 hover:bg-white/10 md:grid-cols-[auto,1fr] md:p-10"
        >
          <div className="flex justify-center md:justify-start">
            <SeriesBadge slug="alamo-series" size="lg" />
          </div>
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Signature Collection · Residential Flagship
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold text-white drop-shadow-lg md:text-4xl">
              Residential lives in the Alamo Series™
            </h2>
            <p className="mt-3 text-lg text-white/90 drop-shadow">
              Every home awning, patio cover, retractable, and drop curtain we build for Texas
              homeowners rolls up under our residential flagship — the Alamo Series™. Same welded
              aluminum, same Sunbrella® canvas, same Seguin crew, one unified warranty.
            </p>
            <span className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-red transition group-hover:gap-3">
              Explore the Alamo Series →
            </span>
          </div>
        </Link>
        <p className="relative mt-4 text-center text-xs text-white/80">
          Shown: custom residential awning in Sunbrella® Beaufort Black &amp; White stripe.
        </p>
      </div>
    </section>
  );
}

function SunbrellaFabricSection() {
  return (
    <section className="bg-white py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <figure className="overflow-hidden border border-navy-deep/10 shadow-card">
            <img
              src={sunbrellaFabric.url}
              alt="Sunbrella® Beaufort Captain Navy awning stripe canvas — a Sunbrella® brand solution-dyed acrylic fabric"
              width={1024}
              height={1024}
              loading="lazy"
              decoding="async"
              className="block h-auto w-full"
            />
            <figcaption className="bg-cream px-4 py-3 text-xs text-navy-deep/70">
              Shown: Sunbrella® Beaufort Captain Navy — a Sunbrella® brand solution-dyed acrylic awning fabric.
              Sunbrella® is a registered trademark of Glen Raven, Inc.
            </figcaption>
          </figure>
          <div>
            <a
              href="https://www.sunbrella.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
              aria-label="Visit Sunbrella.com (opens in a new tab)"
            >
              <img
                src={sunbrellaLogo.url}
                alt="Sunbrella® logo — registered trademark of Glen Raven, Inc."
                width={220}
                height={220}
                loading="lazy"
                decoding="async"
                className="h-20 w-auto"
              />
            </a>
            <div className="mt-4 font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Sunbrella® Canvas · Trusted Materials
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Built with genuine Sunbrella® fabric
            </h2>
            <p className="mt-3 text-navy-deep/80">
              Every Alamo Series™ residential awning is sewn with genuine Sunbrella® solution-dyed
              acrylic canvas. Color goes all the way through each fiber before it's ever woven —
              that's why Sunbrella® fabric resists fading, mildew, and Texas UV year after year,
              backed by a 10-year Sunbrella® limited warranty.
            </p>
            <p className="mt-3 text-xs text-navy-deep/60">
              Sunbrella® is a registered trademark of Glen Raven, Inc. Texas Made Shade &amp;
              Covers™ is an independent fabricator and is not affiliated with Glen Raven, Inc.
            </p>
            <a
              href="https://www.sunbrella.com/browse-fabrics/fabrics-by-use/shade-awnings-pergolas"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90"
            >
              Shop Sunbrella® Showroom — Shade · Awnings · Pergolas →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SunbrellaHowItsMadeSection() {
  return (
    <section className="bg-cream py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-6">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
          ★ How Sunbrella® Fabric Is Made
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
          Solution-dyed from the fiber up
        </h2>
        <div className="mt-4 text-navy-deep/80">
          <figure className="mb-4 w-full overflow-hidden border border-navy-deep/10 bg-black shadow-card sm:float-right sm:mb-3 sm:ml-6 sm:w-72 md:w-80">
            <Link to="/videos/sunbrella-fabric" aria-label="Watch how Sunbrella outdoor fabric is made">
              <img src={watchVideos.sunbrella.poster} width={900} height={900} loading="lazy" alt="Sunbrella fabric manufacturing machinery from the video" className="block h-auto w-full" />
            </Link>
            <figcaption className="bg-white px-3 py-2 text-[11px] text-navy-deep/70">
              How Sunbrella® outdoor fabric is made. Sunbrella® is a registered trademark of
              Glen Raven, Inc.
            </figcaption>
          </figure>
          <p>
            Sunbrella® fabric starts as raw acrylic that's solution-dyed — pigment is locked into
            the fiber itself before it's spun into yarn. That's the difference between canvas
            that fades in two summers and canvas that holds its color for a decade of Texas sun.
          </p>
          <ul className="mt-4 space-y-2">
            <li>• Solution-dyed acrylic fiber — color runs through the yarn, not just the surface</li>
            <li>• Woven and finished for water repellency, mildew resistance, and UV stability</li>
            <li>• Backed by the Sunbrella® 10-year limited warranty on shade &amp; marine fabrics</li>
            <li>• Recyclable through the Sunbrella® Recycle My Sunbrella® program</li>
          </ul>
          <p className="mt-4 text-xs text-navy-deep/60">
            Video courtesy of Glen Raven, Inc. Sunbrella® and Recycle My Sunbrella® are
            registered trademarks of Glen Raven, Inc.
          </p>
          <div className="clear-both" />
        </div>
        <Button asChild className="mt-4 h-auto whitespace-normal"><Link to="/videos/sunbrella-fabric"><Play aria-hidden="true" />Watch How Sunbrella® Fabric Is Made</Link></Button>
      </div>
    </section>
  );
}

function RangerCareSection() {
  return (
    <section className="bg-white py-12 md:py-16">
      <div className="mx-auto max-w-5xl px-6">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
          ★ Ranger Care™ · Homestead Plan
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
          Protect your awning with Ranger Care™
        </h2>
        <div className="mt-6 text-navy-deep/80">
          <img
            src={rangerCareBadge.url}
            alt="Ranger Care Homestead Plan — Residential Shade Care badge"
            className="float-right ml-6 mb-4 w-40 md:w-56 lg:w-64 transition-transform duration-300 hover:rotate-[-3deg] hover:scale-105"
            loading="lazy"
          />
          <img
            src={rangerCareTech.url}
            alt="Texas Made Shade technician measuring a commercial awning frame during a Ranger Care inspection"
            className="float-left mr-6 mb-4 w-44 md:w-60 lg:w-72 rounded-md shadow-md object-cover"
            loading="lazy"
          />
          <p>
            Every Alamo Series™ residential awning is eligible for our Ranger Care™ Homestead Plan —
            an annual membership that keeps your welded frame, Sunbrella® canvas, and hardware in
            working order. One yearly on-site visit from the same Seguin crew that built it, plus
            priority scheduling when Texas weather does what Texas weather does.
          </p>
          <ul className="mt-4 space-y-2 text-navy-deep/85">
            <li>• Annual inspection of frame, welds, fabric &amp; hardware</li>
            <li>• Fabric cleaning &amp; UV re-treatment recommendations</li>
            <li>• Front-of-line scheduling for repairs &amp; storm damage</li>
            <li>• Member discount on parts, labor &amp; fabric replacement</li>
            <li>• Extended warranty coverage on qualifying builds</li>
          </ul>
          <div className="clear-right" />
          <Link
            to="/ranger-care"
            className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red"
          >
            Learn About Ranger Care™ →
          </Link>
        </div>
      </div>
    </section>
  );
}


function FinancingSection() {
  return (
    <section id="financing" className="bg-cream py-14">
      <div className="mx-auto max-w-4xl px-4 lg:px-8 text-center">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ FINANCING AVAILABLE ★</div>
        <h2 className="mt-2 font-display text-2xl font-bold uppercase md:text-3xl text-navy-deep">
          Affordable Payment Options In Less Than A Minute
        </h2>
        <p className="mt-3 text-navy-deep/80">
          We help you find affordable payment options in less than a minute. Compare personalized offers
          from lenders in our network — <strong>with no impact to your credit score</strong>. Explore{" "}
          <a
            href="https://fs.acornfinance.com/assets/portal/images/sharing/facebook/facebook-exterior-remodel-1.html?d=WZ1XX"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-red underline underline-offset-4 hover:text-red-deep"
          >
            Financing
          </a>{" "}
          through Acorn Finance and its lender network.
        </p>
        <a
          href="https://fs.acornfinance.com/assets/portal/images/sharing/facebook/facebook-exterior-remodel-1.html?d=WZ1XX"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Check my financing offers with Acorn Finance (opens in a new tab)"
          className="mt-6 inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-red-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2"
        >
          Check My Offers →
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>
    </section>
  );
}




export const Route = createFileRoute("/residential-awnings")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Residential Awning Company — San Antonio Patio, Window & Porch Awnings | Texas Made Shade",
      description: "Residential awning company serving San Antonio, Austin & South Texas — patio, window, porch and door awnings. Part of the Alamo Series™ residential flagship.",
      image: shareImage,
      imageAlt: "Custom residential patio awning in Texas",
      socialTitle: "Residential Awnings — Alamo Series™ | Texas Made Shade",
      socialDescription: "Patio, window and porch awnings for Texas homes — the Alamo Series™ residential flagship.",
      keywords: "residential awnings Texas, patio awnings, window awnings, home awnings San Antonio, residential awnings Austin, alamo series awnings",
    }),
    scripts: serviceHeadScripts({
      path: "/residential-awnings",
      serviceName: "Residential Awnings",
      breadcrumbName: "Residential Awnings",
      description: "Custom residential awnings — patio, window and door — built with welded aluminum frames and Sunbrella canvas for Texas homes.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/residential-awnings"
      serviceName="Residential Awnings"
      eyebrow="★ RESIDENTIAL AWNINGS · ALAMO SERIES™ ★"
      title="Residential Awnings — Patio, Window, Porch & Door"
      intro="Custom residential awnings for Texas homes. Welded aluminum frames with Sunbrella® canvas — designed to match your home's style and built to handle Texas sun, wind and rain. Part of the Alamo Series™, our residential flagship."
      heroImage={blackStripeWindowAwnings}
      preContent={<><AlamoSeriesCallout /><SunbrellaFabricSection /><SunbrellaHowItsMadeSection /><RangerCareSection /><FinancingSection /></>}
      hideLetsTalkForm
      features={[
        "Patio, window, porch and door awnings",
        "Custom welded aluminum frames — no prefab kits",
        "Sunbrella® canvas with 10-year color-fast warranty",
        "HOA-compliant colors, shapes and styles",
        "Cuts indoor cooling costs by shading windows",
        "Built and installed by the same Texas crew",
      ]}
      body={[
        { heading: "Patio & Porch Awnings", text: "Extend your living space outdoors with a custom patio or porch awning. Welded aluminum frame, premium Sunbrella® canvas, designed to match your home's lines.", image: { src: backyardMetalCover, alt: "Custom back porch retractable awning in Sunbrella® Smoke fabric shading a stained concrete patio on a Texas ranch-style home", caption: "Back porch awning · Sunbrella® Smoke" } },
        { heading: "Window & Door Awnings", text: "Classic Texas-style window awnings shade interiors, cut AC costs, and add curb appeal. Available in stripes, solids, and HOA-friendly tones.", image: { src: blackStripeWindowAwnings, alt: "Pair of custom black-and-cream pinstripe Sunbrella window awnings fabricated and installed by Texas Made Shade over the upstairs windows of a green board-and-batten Texas home, with the Texas Made Shade & Covers work truck parked in the driveway", caption: "Black pinstripe window awnings — custom fit and installed by our Seguin crew" } },
        { heading: "HOA-Friendly Design", text: "Live in an HOA neighborhood? We'll build you a custom 3D rendering of your awning — frame profile, projection, pitch and canvas color — that you can submit directly to your HOA architectural committee for approval. We help homeowners pick canvas colors, frame profiles and mounting styles that meet HOA requirements while still looking great.", image: { src: hoa3dDesign.url, alt: "3D CAD rendering of a custom residential awning design Texas Made Shade produces for HOA architectural review and approval", caption: "3D design rendering — submit to your HOA for approval" } },
        { heading: "Custom Measured On-Site", text: "Every residential awning starts with hand measurements taken on-site at your home. We tape every window, door and patio opening ourselves — width, height, projection and mounting surface — so the welded frame and canvas land tight to the wall with no gaps, sagging or guesswork. No two Texas homes are the same, and an awning that fits right is an awning that lasts.", image: { src: customMeasuring, alt: "Texas Made Shade team member measuring a storefront window sill with a tape measure for a custom-fit awning in Texas" , caption: "Hand-measured on-site — every awning is patterned to your exact opening." } },
      ]}
      applications={["Patios", "Porches", "Windows", "Doors", "Walkways", "RV Pads", "Outdoor Kitchens"]}
      faqs={[
        { q: "Will a residential awning lower my AC bill?", a: "Yes. Window and patio awnings block direct sunlight before it hits the glass, which dramatically reduces solar heat gain. Customers commonly report noticeable indoor temperature and AC-runtime improvements." },
        { q: "Are your residential awnings HOA-compliant?", a: "We work with you to choose canvas colors, frame styles and mounting profiles that meet your HOA's requirements. We've installed in dozens of HOA-governed neighborhoods across Texas." },
        { q: "What areas do you cover for residential installs?", a: "Seguin, San Antonio, New Braunfels, Austin, Boerne, Kerrville, Fredericksburg, Corpus Christi, and most of Central & South Texas." },
        { q: "How long does a residential awning last?", a: "Welded aluminum frames last 20+ years. Sunbrella canvas typically holds 8–12 years in Texas sun before needing re-cover." },
        { q: "Is residential part of the Alamo Series™?", a: "Yes. The Alamo Series™ is our residential flagship — awnings, retractables, patio covers, sail shades, and drop curtains for Texas homeowners, all built and installed by the same crew out of our Seguin shop." },
      ]}
      postCta={<PergolaUpgradeCta />}
    >
      <LetsTalkForm
        imageSrc={awningUnderneathAsset.url}
        imageAlt="Underside view of a welded aluminum awning frame Texas Made Shade fabricates and recovers for Texas homes"
        eyebrow="Let's Talk — Alamo Series™ Residential"
        heading="Get a free residential awning estimate"
        intro="Send us a few measurements and photos of your home and we'll put together a custom quote — welded aluminum frame, Sunbrella® canvas, installed by our Seguin crew."
        extraFieldsSummary={[
          { name: "awningWidth", label: "How Wide" },
          { name: "awningHeight", label: "How Tall in Back" },
          { name: "awningProjection", label: "How Far Out From Wall" },
          { name: "awningSeries", label: "Series / Market", multiple: true },
          { name: "awningFabric", label: "Preferred Fabric" },
        ]}
        extraFields={<AwningExtras />}
      />
    </ServicePage>
  ),
});
